import { useMutation } from "@tanstack/react-query"
import { resetPassword } from "api/auth.api.ts"
import { useToast } from "hooks/index.js"
import { useEffect, useState } from "react"
import styled from "styled-components"
import { AppButton } from "ui/base/appButton/AppButton.jsx"
import { AppButtonAuth } from "ui/base/appButton/AppButtonAuth.jsx"
import { HelperText } from "ui/base/helperText/HelperText"
import { InputSignUp } from "ui/base/InputSignUp/InputSignUp"
import { LockIcon } from "ui/icons/index.js"
import { passwordValidation, removeEmojis } from "utils"

export const ResetPassword = ({
    email,
    setActiveTab,
}) => {

    const [formError, setFormError] = useState({
        newPass: false,
        confirmPass: false,
        errorText: "",
    })
    const [newPass, setnewPass] = useState("")
    const [confirmPass, setConfirmPass] = useState("")

    const { mutate, isLoading, isSuccess } = useMutation({
        mutationFn: resetPassword,

        onError: (err) => {
            useToast(
                "error",
                error.data?.message ||
                "Password reset action has been timed out. Please try again."
            )
        },
    })

    useEffect(() => {
        if (isSuccess) {
            setActiveTab("ResetSuccess")
        }
    }, [isSuccess])

    const validatePass = () => {
        if (newPass !== confirmPass) {
            setFormError(() => ({
                newPass: true,
                confirmPass: true,
                errorText: "Passwords don't match",
            }))
        } else if (!passwordValidation(newPass)) {
            setFormError(() => ({
                newPass: true,
                confirmPass: true,
                errorText: "Passwords don't follow the given criteria",
            }))
        } else if (newPass.trim().length > 0 && confirmPass.trim().length > 0) {
            mutate({
                email: email,
                new_password: newPass,
                confirm_password: confirmPass,
            })
        }
    }

    if (!email) {
        return null
    }

    return (
        <LoginLeftContainer>
            <LoginTextContainer>
                <HelperText
                    size='h5'
                    sizeMobile='h5'
                    weight={600}
                    centerMobile
                    center
                    style={{ whiteSpace: "nowrap" }}>
                    Password reset
                </HelperText>
                <HelperText centerMobile center size='small' sizeMobile='small'>
                    Password must be a combination of 6-20 characters with at least 1
                    upper case, 1 lower case, 1 numeric and 1 special character. Spaces
                    are not allowed.
                </HelperText>
            </LoginTextContainer>

            <LoginFormContainer>
                <InputSignUp
                    id='password'
                    type='password'
                    placeholder='New password'
                    icon={<LockIcon />}
                    value={newPass}
                    setValue={(input) => {
                        setnewPass(removeEmojis(input.trim()))
                        if (input.trim() === "") {
                            setFormError((prevState) => ({
                                ...prevState,
                                newPass: true,
                                errorText: "Fill this field",
                            }))
                        } else {
                            setFormError((prevState) => ({
                                ...prevState,
                                newPass: false,
                                errorText: "Fill this field",
                            }))
                            setFormError((prevState) => ({
                                ...prevState,
                                confirmPass: false,
                            }))
                        }
                    }}
                    maxLength={20}
                    isError={formError.newPass}
                    errorText={formError.errorText}
                />
                <InputSignUp
                    id='confirmPassword'
                    type='password'
                    placeholder='Confirm new password'
                    icon={<LockIcon />}
                    value={confirmPass}
                    setValue={(input) => {
                        setConfirmPass(removeEmojis(input.trim()))
                        if (input.trim() === "") {
                            setFormError((prevState) => ({
                                ...prevState,
                                confirmPass: true,
                            }))
                        } else {
                            setFormError((prevState) => ({
                                ...prevState,
                                confirmPass: false,
                            }))
                            setFormError((prevState) => ({
                                ...prevState,
                                newPass: false,
                            }))
                        }
                    }}
                    maxLength={20}
                    isError={formError.confirmPass}
                    errorText={formError.errorText}
                />

                <AppButtonAuth disabled={isLoading} onClick={validatePass}>
                    Reset Password
                </AppButtonAuth>
            </LoginFormContainer>
        </LoginLeftContainer>
    )
}

const LoginLeftContainer = styled.div`
  display: grid;
  gap: 50px;
  width: 100%;
  max-width: 450px;
  margin-inline: auto;
  font-family: var(--font-secondary);
  margin-top: 2dvh;
  @media (min-width: 768px) {
    margin-top: 0px;
  }
`

const LoginTextContainer = styled.div`
  display: grid;
  gap: 1rem;
`

const LoginFormContainer = styled.div`
  display: grid;
  gap: 1rem;
`
